import React, { createContext, useEffect, useMemo, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { compact } from 'lodash-es'
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog.tsx'
import { DateFormat } from '@/components/date-format.tsx'
import { useFindAttachments } from '@/features/attachments/hooks/use-find-attachments.ts'
import {
  downloadFile,
  useDownloadFile,
} from '@/features/files/hooks/use-download-file.ts'

interface OpenAttachments {
  attachmentIds: string[]
  currentIndex?: number
}

// Create context
const ViewAttachmentsContext = createContext<{
  openAttachments: (options: OpenAttachments) => void
}>({
  openAttachments: () => {},
})

// Export function to open attachments
let openAttachmentsInstance: ((options: OpenAttachments) => void) | null = null

function registerOpenAttachments(
  openAttachments: (options: OpenAttachments) => void
) {
  openAttachmentsInstance = openAttachments
}

export function openAttachments(options: OpenAttachments) {
  if (!openAttachmentsInstance) {
    throw new Error(
      'Attachments viewer not initialized. Make sure to wrap your app with ViewAttachmentsProvider.'
    )
  }
  return openAttachmentsInstance(options)
}

// Provider component
export function ViewAttachmentsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [attachmentIds, setAttachmentIds] = useState<
    Array<string> | null | undefined
  >()
  const attachmentsQuery = useFindAttachments(
    {
      id: { in: attachmentIds as Array<string> },
    },
    { enabled: !!attachmentIds?.length }
  )

  const attachments = useMemo(
    () =>
      compact(
        attachmentIds?.map((id) =>
          (attachmentsQuery?.data?.data ?? []).find((a) => a.id === id)
        )
      ),
    [attachmentIds, attachmentsQuery?.data?.data]
  )
  const currentAttachment = attachments[currentIndex]

  const downloadFileQuery = useDownloadFile(
    { fileId: currentAttachment?.file_id, contentDisposition: 'inline' },
    { enabled: !!currentAttachment?.file_id }
  )
  const downloadUrl = downloadFileQuery.data?.success
    ? downloadFileQuery.data?.data.downloadUrl
    : null

  const openAttachments = ({
    attachmentIds,
    currentIndex,
  }: OpenAttachments) => {
    // Fetch attachments based on attachmentIds
    // Replace this mock data with actual API calls or data fetching logic

    setCurrentIndex(currentIndex ?? 0)
    setAttachmentIds(attachmentIds)
    setIsOpen(true)
  }

  useEffect(() => {
    registerOpenAttachments(openAttachments)
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        navigatePrevious()
      } else if (event.key === 'ArrowRight') {
        navigateNext()
      } else if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const navigatePrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : attachments.length - 1
    )
  }

  const navigateNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < attachments.length - 1 ? prevIndex + 1 : 0
    )
  }

  return (
    <ViewAttachmentsContext.Provider value={{ openAttachments }}>
      {children}
      <Dialog open={isOpen && !!currentAttachment} onOpenChange={setIsOpen}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 grid h-screen max-h-full w-screen -translate-x-1/2 -translate-y-1/2 gap-4 !rounded-none border bg-background p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between bg-muted p-4">
                <div>
                  <h2 className="mb-2 text-xl font-semibold">
                    {currentAttachment?.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Data upload:{' '}
                    <DateFormat date={currentAttachment?.created_at} />
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/*<DropdownMenu>*/}
                  {/*  <DropdownMenuTrigger asChild>*/}
                  {/*    <Button*/}
                  {/*      variant="ghost"*/}
                  {/*      size="icon"*/}
                  {/*      aria-label="More options"*/}
                  {/*    >*/}
                  {/*      <MoreVertical className="size-4" />*/}
                  {/*    </Button>*/}
                  {/*  </DropdownMenuTrigger>*/}
                  {/*  <DropdownMenuContent align="end">*/}
                  {/*    <DropdownMenuItem onSelect={handlePrint}>*/}
                  {/*      <Printer className="mr-2 size-4" />*/}
                  {/*      <span>Print</span>*/}
                  {/*    </DropdownMenuItem>*/}
                  {/*    <DropdownMenuItem onSelect={handleDownload}>*/}
                  {/*      <Download className="mr-2 size-4" />*/}
                  {/*      <span>Download</span>*/}
                  {/*    </DropdownMenuItem>*/}
                  {/*  </DropdownMenuContent>*/}
                  {/*</DropdownMenu>*/}
                  <div className="flex items-center gap-4">
                    <p className="mt-1 text-sm text-muted-foreground">
                      {currentIndex + 1} di {attachments.length}
                    </p>

                    <div>
                      <Button
                        className="[&_svg]:size-6"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          downloadFile({
                            fileId: currentAttachment?.file_id,
                            downloadName: currentAttachment.name,
                          })
                        }
                        aria-label="Download"
                      >
                        <Download />
                      </Button>
                      <Button
                        className="[&_svg]:size-6"
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsOpen(false)}
                        aria-label="Chiudi"
                      >
                        <X />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative flex-1 overflow-hidden">
                {downloadUrl && (
                  <iframe
                    className="size-full object-contain"
                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                      downloadUrl
                    )}&embedded=true`}
                  />
                )}

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                  onClick={navigatePrevious}
                  aria-label="Previous attachment"
                >
                  <ChevronLeft className="size-8" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  onClick={navigateNext}
                  aria-label="Next attachment"
                >
                  <ChevronRight className="size-8" />
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
      {/*{isOpen && currentAttachment? && (*/}
      {/*    <div className="fixed inset-0 z-50 flex flex-col bg-background">*/}
      {/*      <div className="flex items-center justify-between bg-muted p-4">*/}
      {/*        <h2 className="text-xl font-semibold">{currentAttachment.name}</h2>*/}
      {/*        <div className="flex items-center gap-2">*/}
      {/*          <DropdownMenu>*/}
      {/*            <DropdownMenuTrigger asChild>*/}
      {/*              <Button variant="ghost" size="icon" aria-label="More options">*/}
      {/*                <MoreVertical className="size-4"/>*/}
      {/*              </Button>*/}
      {/*            </DropdownMenuTrigger>*/}
      {/*            <DropdownMenuContent align="end">*/}
      {/*              <DropdownMenuItem onSelect={handlePrint}>*/}
      {/*                <Printer className="mr-2 size-4"/>*/}
      {/*                <span>Print</span>*/}
      {/*              </DropdownMenuItem>*/}
      {/*              <DropdownMenuItem onSelect={handleDownload}>*/}
      {/*                <Download className="mr-2 size-4"/>*/}
      {/*                <span>Download</span>*/}
      {/*              </DropdownMenuItem>*/}
      {/*            </DropdownMenuContent>*/}
      {/*          </DropdownMenu>*/}
      {/*          <Button*/}
      {/*              variant="ghost"*/}
      {/*              size="icon"*/}
      {/*              onClick={() => setIsOpen(false)}*/}
      {/*              aria-label="Close viewer"*/}
      {/*          >*/}
      {/*            <X className="size-6"/>*/}
      {/*          </Button>*/}
      {/*        </div>*/}
      {/*      </div>*/}

      {/*      <div className="relative flex-1">*/}
      {/*        <img*/}
      {/*            src={currentAttachment.url}*/}
      {/*            alt={`Attachment: ${currentAttachment.name}`}*/}
      {/*            className="size-full object-contain"*/}
      {/*        />*/}

      {/*        <Button*/}
      {/*            variant="ghost"*/}
      {/*            size="icon"*/}
      {/*            className="absolute left-4 top-1/2 -translate-y-1/2"*/}
      {/*            onClick={navigatePrevious}*/}
      {/*            aria-label="Previous attachment"*/}
      {/*        >*/}
      {/*          <ChevronLeft className="size-8"/>*/}
      {/*        </Button>*/}

      {/*        <Button*/}
      {/*            variant="ghost"*/}
      {/*            size="icon"*/}
      {/*            className="absolute right-4 top-1/2 -translate-y-1/2"*/}
      {/*            onClick={navigateNext}*/}
      {/*            aria-label="Next attachment"*/}
      {/*        >*/}
      {/*          <ChevronRight className="size-8"/>*/}
      {/*        </Button>*/}
      {/*      </div>*/}

      {/*      <div className="bg-muted p-4">*/}
      {/*        <p className="text-sm text-muted-foreground">*/}
      {/*          Uploaded on:{' '}*/}
      {/*          {new Date(currentAttachment.uploadDate).toLocaleDateString()}*/}
      {/*        </p>*/}
      {/*        <p className="mt-1 text-sm text-muted-foreground">*/}
      {/*        {currentIndex + 1} of {attachments.length}*/}
      {/*      </p>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*)}*/}
    </ViewAttachmentsContext.Provider>
  )
}
